import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { checkPermissions } from "../../../Services/User/User";
import {
  createOnlineContact,
  createOnlineFaq,
  deleteOnlineContact,
  deleteOnlineFaq,
  getOnlineContact,
  getOnlineFaqs,
  updateOnlineContact,
  updateOnlineFaq,
} from "../../../Services/OnlineContentService";
import "./onlineContent.css";

const emptyContact = { email: "", phone: "" };
const emptyFaq = { question: "", answer: "" };

const apiErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

const OnlineContent = () => {
  const navigate = useNavigate();
  const [contact, setContact] = useState(emptyContact);
  const [contactValues, setContactValues] = useState(emptyContact);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactDeleting, setContactDeleting] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqSaving, setFaqSaving] = useState(false);
  const [faqDeleting, setFaqDeleting] = useState(null);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [faqForm, setFaqForm] = useState(emptyFaq);
  const [editingFaqId, setEditingFaqId] = useState(null);

  const loadContact = useCallback(async () => {
    setContactLoading(true);
    try {
      const response = await getOnlineContact();
      if (!response?.status) {
        throw new Error(response?.message || "Unable to load contact details.");
      }

      const values = {
        email: response.data?.email || "",
        phone: response.data?.phone || "",
      };
      setContact(values);
      setContactValues(values);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to load contact details."));
    } finally {
      setContactLoading(false);
    }
  }, []);

  const loadFaqs = useCallback(async () => {
    setFaqLoading(true);
    try {
      const response = await getOnlineFaqs();
      if (!response?.status) {
        throw new Error(response?.message || "Unable to load FAQs.");
      }
      if (!Array.isArray(response.data)) {
        throw new Error("The FAQ response has an unexpected format.");
      }
      setFaqs(response.data);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to load FAQs."));
    } finally {
      setFaqLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!checkPermissions("system_settings_page")) {
      navigate("/", { replace: true });
      return;
    }
    loadContact();
    loadFaqs();
  }, [loadContact, loadFaqs, navigate]);

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    const changedKeys = Object.keys(contactValues).filter(
      (key) => contactValues[key].trim() !== contact[key].trim()
    );
    if (changedKeys.length === 0) {
      toast.info("There are no contact details to save.");
      return;
    }

    if (changedKeys.some((key) => !contactValues[key].trim())) {
      toast.error("Use the delete button to remove a saved contact value.");
      return;
    }

    const newValues = {};
    const existingValues = {};
    changedKeys.forEach((key) => {
      if (contact[key]) {
        existingValues[key] = contactValues[key].trim();
      } else {
        newValues[key] = contactValues[key].trim();
      }
    });

    setContactSaving(true);
    let changedSuccessfully = false;
    try {
      if (Object.keys(newValues).length > 0) {
        const response = await createOnlineContact(newValues);
        if (!response?.status) {
          throw new Error(response?.message || "Unable to create contact details.");
        }
        changedSuccessfully = true;
      }
      if (Object.keys(existingValues).length > 0) {
        const response = await updateOnlineContact(existingValues);
        if (!response?.status) {
          throw new Error(response?.message || "Unable to update contact details.");
        }
        changedSuccessfully = true;
      }
      toast.success("Contact details saved successfully.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to save contact details."));
    } finally {
      if (changedSuccessfully) {
        await loadContact();
      }
      setContactSaving(false);
    }
  };

  const handleContactDelete = async (key) => {
    if (!window.confirm(`Delete the saved ${key}?`)) {
      return;
    }

    setContactDeleting(key);
    try {
      const response = await deleteOnlineContact([key]);
      if (!response?.status) {
        throw new Error(response?.message || `Unable to delete ${key}.`);
      }
      toast.success(`${key === "email" ? "Email" : "Phone"} deleted successfully.`);
      await loadContact();
    } catch (error) {
      toast.error(apiErrorMessage(error, `Unable to delete ${key}.`));
    } finally {
      setContactDeleting("");
    }
  };

  const handleFaqSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      question: faqForm.question.trim(),
      answer: faqForm.answer.trim(),
    };

    setFaqSaving(true);
    try {
      const response = editingFaqId
        ? await updateOnlineFaq(editingFaqId, payload)
        : await createOnlineFaq(payload);
      if (!response?.status) {
        throw new Error(response?.message || "Unable to save FAQ.");
      }
      toast.success(editingFaqId ? "FAQ updated successfully." : "FAQ created successfully.");
      setFaqForm(emptyFaq);
      setEditingFaqId(null);
      setShowFaqForm(false);
      await loadFaqs();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to save FAQ."));
    } finally {
      setFaqSaving(false);
    }
  };

  const handleFaqEdit = (faq) => {
    setFaqForm({
      question: faq.question || "",
      answer: faq.answer || "",
    });
    setEditingFaqId(faq.id);
    setShowFaqForm(true);
  };

  const resetFaqForm = () => {
    setFaqForm(emptyFaq);
    setEditingFaqId(null);
    setShowFaqForm(false);
  };

  const handleFaqDelete = async (faq) => {
    if (!window.confirm("Delete this FAQ?")) {
      return;
    }

    setFaqDeleting(faq.id);
    try {
      const response = await deleteOnlineFaq(faq.id);
      if (!response?.status) {
        throw new Error(response?.message || "Unable to delete FAQ.");
      }
      toast.success("FAQ deleted successfully.");
      if (editingFaqId === faq.id) {
        resetFaqForm();
      }
      await loadFaqs();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to delete FAQ."));
    } finally {
      setFaqDeleting(null);
    }
  };

  return (
    <main id="main" className="main online-content-page">
      <div className="pagetitle">
        <h1>Online Content</h1>
        <p>Manage public contact details and frequently asked questions.</p>
      </div>

      <section className="section">
        <div className="row gy-4">
          <div className="col-12">
            <div className="card online-content-card">
              <div className="card-body">
                <div className="online-content-heading">
                  <div>
                    <h2><i className="bi bi-person-lines-fill" /> Contact Details</h2>
                    <p>These details are shown to visitors on the public site.</p>
                  </div>
                </div>

                {contactLoading ? (
                  <div className="text-center py-4">
                    <span className="spinner-border text-primary" role="status" />
                    <div className="mt-2">Loading contact details...</div>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit}>
                    <div className="row g-3 align-items-end">
                      <div className="col-lg-5">
                        <label className="form-label" htmlFor="online-contact-email">Email</label>
                        <div className="input-group">
                          <input
                            id="online-contact-email"
                            className="form-control"
                            type="email"
                            value={contactValues.email}
                            onChange={(event) => setContactValues({
                              ...contactValues,
                              email: event.target.value,
                            })}
                            placeholder="support@example.com"
                          />
                          {contact.email && (
                            <button
                              className="btn btn-outline-danger"
                              type="button"
                              disabled={Boolean(contactDeleting)}
                              onClick={() => handleContactDelete("email")}
                              aria-label="Delete email"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-5">
                        <label className="form-label" htmlFor="online-contact-phone">Phone</label>
                        <div className="input-group">
                          <input
                            id="online-contact-phone"
                            className="form-control"
                            type="tel"
                            value={contactValues.phone}
                            onChange={(event) => setContactValues({
                              ...contactValues,
                              phone: event.target.value,
                            })}
                            placeholder="+1 555 0100"
                          />
                          {contact.phone && (
                            <button
                              className="btn btn-outline-danger"
                              type="button"
                              disabled={Boolean(contactDeleting)}
                              onClick={() => handleContactDelete("phone")}
                              aria-label="Delete phone"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-2">
                        <button className="btn btn-primary w-100" type="submit" disabled={contactSaving}>
                          {contactSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card online-content-card">
              <div className="card-body">
                <div className="online-content-heading">
                  <div>
                    <h2><i className="bi bi-question-circle" /> FAQs</h2>
                    <p>Manage frequently asked questions shown to visitors.</p>
                  </div>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      resetFaqForm();
                      setShowFaqForm(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-2" />
                    Add FAQ
                  </button>
                </div>

                <div className="online-faq-list">
                  {faqLoading ? (
                    <div className="text-center py-5">
                      <span className="spinner-border text-primary" role="status" />
                      <div className="mt-2">Loading FAQs...</div>
                    </div>
                  ) : faqs.length > 0 ? (
                    <div className="accordion" id="onlineFaqAccordion">
                      {faqs.map((faq, index) => (
                        <div
                          className="accordion-item border mb-2 rounded overflow-hidden"
                          key={faq.id}
                        >
                          <h3 className="accordion-header" id={`online-faq-heading-${faq.id}`}>
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#online-faq-collapse-${faq.id}`}
                              aria-expanded="false"
                              aria-controls={`online-faq-collapse-${faq.id}`}
                            >
                              <span className="badge bg-primary me-3">#{index + 1}</span>
                              <span className="fw-semibold">{faq.question}</span>
                            </button>
                          </h3>
                          <div
                            id={`online-faq-collapse-${faq.id}`}
                            className="accordion-collapse collapse"
                            aria-labelledby={`online-faq-heading-${faq.id}`}
                            data-bs-parent="#onlineFaqAccordion"
                          >
                            <div className="accordion-body">
                              <div className="mb-3 text-muted online-faq-answer">{faq.answer}</div>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  type="button"
                                  onClick={() => handleFaqEdit(faq)}
                                >
                                  <i className="bi bi-pencil-square me-1" /> Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  type="button"
                                  disabled={faqDeleting === faq.id}
                                  onClick={() => handleFaqDelete(faq)}
                                >
                                  <i className="bi bi-trash me-1" />
                                  {faqDeleting === faq.id ? "Deleting..." : "Delete"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="online-faq-empty">
                      <i className="bi bi-chat-square-text" />
                      <span>No FAQs have been added yet.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showFaqForm && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !faqSaving) {
                resetFaqForm();
              }
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <form className="modal-content" onSubmit={handleFaqSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">{editingFaqId ? "Edit FAQ" : "Create FAQ"}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={resetFaqForm}
                    disabled={faqSaving}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" htmlFor="online-faq-question">
                      Question
                    </label>
                    <input
                      id="online-faq-question"
                      className="form-control"
                      value={faqForm.question}
                      onChange={(event) => setFaqForm({ ...faqForm, question: event.target.value })}
                      placeholder="Enter FAQ question"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" htmlFor="online-faq-answer">
                      Answer
                    </label>
                    <textarea
                      id="online-faq-answer"
                      className="form-control"
                      rows="6"
                      value={faqForm.answer}
                      onChange={(event) => setFaqForm({ ...faqForm, answer: event.target.value })}
                      placeholder="Enter FAQ answer"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={resetFaqForm}
                    disabled={faqSaving}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={faqSaving}>
                    {faqSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2" />
                        {editingFaqId ? "Update FAQ" : "Save FAQ"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </main>
  );
};

export default OnlineContent;
